import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Internal Documents / e-sign subsystem (Phase 2B). Admin endpoints are gated
// server-side by documents:manage; the public signer endpoints are tokenized.

export const useFetchTemplates = (params = {}) =>
  useQuery(['documents', 'templates', params], async () => {
    const res = await axios.get('/documents/templates', { params });
    return res.data.templates;
  });

// The grouped Documents-page view: processes (each w/ ordered docs) + uncategorized.
export const useFetchDocumentsGrouped = () =>
  useQuery(['documents', 'grouped'], async () => {
    const res = await axios.get('/documents/grouped');
    return res.data; // { processes: [...], uncategorized: [...] }
  });

export const useSetProcessDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ processId, documentIds }) =>
      (await axios.put(`/documents/processes/${processId}/documents`, { documentIds })).data,
    { onSuccess: () => queryClient.invalidateQueries(['documents', 'grouped']) },
  );
};

export const useFetchTemplate = (documentId) =>
  useQuery(
    ['documents', 'template', documentId],
    async () => (await axios.get(`/documents/templates/${documentId}`)).data.template,
    // Don't refetch on window refocus — it churns the builder/preview re-render.
    { enabled: Boolean(documentId), refetchOnWindowFocus: false, staleTime: Infinity },
  );

// The builder's Preview with a sample fee chart drawn in (2026-10-25): the
// server burns a 40% client's chart into the template with these placements
// and sends the PDF back; this hands the builder a local object URL for it
// (revoke it when the preview closes).
export const fetchTemplatePreviewUrl = async (documentId, fieldPlacements) => {
  const { pdfBase64, sample } = (
    await axios.post(`/documents/templates/${documentId}/preview-pdf`, { fieldPlacements })
  ).data;
  const bytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
  return { url: URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), sample };
};

export const useFetchTemplatePdfUrl = (documentId) =>
  useQuery(
    ['documents', 'template-pdf', documentId],
    async () => (await axios.get(`/documents/templates/${documentId}/pdf`)).data,
    // The endpoint returns a temporary URL; refetching on focus yields a NEW url,
    // which re-runs the PDF load effect and reloads the doc. Fetch once, keep it.
    { enabled: Boolean(documentId), refetchOnWindowFocus: false, staleTime: Infinity },
  );

// Imperative: fetch a signed envelope's temporary view URL and open it in a new
// tab. Used by the Referral Payouts + Partners pages to view signed W-9 /
// agreement PDFs. Throws on failure so callers can surface a toast.
// The tab is opened synchronously (before the await) — Safari blocks
// window.open calls that happen after an async gap — then pointed at the
// presigned URL once it arrives (or closed again on failure).
export const openSignedEnvelopePdf = async (envelopeId) => {
  if (!envelopeId) throw new Error('No document to open');
  const win = window.open('', '_blank');
  try {
    const res = await axios.get(`/documents/envelopes/${envelopeId}/signed-pdf`);
    const url = res.data?.url;
    if (!url) throw new Error('The document URL was not returned');
    if (win) {
      win.location = url;
    } else {
      // Popup was blocked even for the synchronous open — best-effort fallback.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (err) {
    if (win) win.close();
    throw err;
  }
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ file, name, description }) => {
      const fd = new FormData();
      fd.append('pdf', file);
      fd.append('name', name);
      if (description) fd.append('description', description);
      const res = await axios.post('/documents/templates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.template;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
      },
    },
  );
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentId, ...body }) =>
      (await axios.put(`/documents/templates/${documentId}`, body)).data.template,
    {
      onSuccess: (template) => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
        if (template?.documentId) {
          queryClient.invalidateQueries(['documents', 'template', template.documentId]);
        }
      },
    },
  );
};

export const useArchiveTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (documentId) =>
      (await axios.post(`/documents/templates/${documentId}/archive`)).data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
      },
    },
  );
};

// Restore an archived template back to active.
export const useRestoreTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (documentId) =>
      (await axios.post(`/documents/templates/${documentId}/unarchive`)).data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
      },
    },
  );
};

// Permanently delete a template (blocked server-side if it has signing history).
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (documentId) => (await axios.delete(`/documents/templates/${documentId}`)).data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
      },
    },
  );
};

// ---- export / import / swap (template portability across environments) ----

// Imperative: download a template as a portable .json bundle (PDF embedded). The
// endpoint returns the bundle JSON; we wrap it in a Blob to trigger a download
// (a raw link wouldn't carry the auth header). Throws so callers can toast.
export const exportTemplateBundle = async (documentId, name = 'document') => {
  const res = await axios.get(`/documents/templates/${documentId}/export`);
  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${String(name || 'document').replace(/[^\w.-]+/g, '_') || 'document'}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Import a .json bundle as a NEW template in this environment.
export const useImportTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (file) => {
      const fd = new FormData();
      fd.append('bundle', file);
      const res = await axios.post('/documents/templates/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
      },
    },
  );
};

// Swap an existing template's underlying PDF. `file` is a raw PDF or an exported
// .json bundle. A 409 with body.warning === 'page_shrink' means kept fields fall
// off a shortened PDF — re-submit with confirmShrink to drop them.
export const useSwapTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentId, file, keepFields = true, confirmShrink = false }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('keepFields', keepFields ? 'true' : 'false');
      if (confirmShrink) fd.append('confirmShrink', 'true');
      const res = await axios.post(`/documents/templates/${documentId}/swap`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['documents', 'templates']);
        queryClient.invalidateQueries(['documents', 'grouped']);
        const id = data?.template?.documentId;
        if (id) {
          queryClient.invalidateQueries(['documents', 'template', id]);
          queryClient.invalidateQueries(['documents', 'template-pdf', id]);
        }
      },
    },
  );
};

// Send a template for signature -> returns { envelopeId, token, signUrl }.
export const useSendEnvelope = () =>
  useMutation(async (body) => (await axios.post('/documents/envelopes', body)).data);

// ---- public signer (tokenized, no auth) ----

// Time limits for the signer (Nic 2026-10-24): with none, a stalled
// connection spun forever. Opening is quick; a submit burns the signed PDF
// and stores it, so it gets longer. A submit that times out but landed
// server-side comes back as a 409 on retry, which the signer treats as done.
export const SIGN_LOAD_TIMEOUT_MS = 30000;
export const SIGN_SUBMIT_TIMEOUT_MS = 120000;
// Whether a failed request is worth trying again: no answer at all (offline,
// timed out) or a server hiccup. A bad, expired or voided link never is.
export const isRetryableSignError = (err) => !err?.response || err.response.status >= 500;

// Multi-document signing session (Phase 2C2): one token -> ordered documents.
export const useFetchSignSession = (token) =>
  useQuery(
    ['documents', 'sign-session', token],
    async () => (await axios.get(`/public/sign/${token}`, { timeout: SIGN_LOAD_TIMEOUT_MS })).data,
    {
      enabled: Boolean(token),
      // A weak signal gets two quiet retries before the signer sees anything.
      retry: (count, err) => count < 2 && isRetryableSignError(err),
      // The session is static while the signer fills it out, and pdfUrl is a
      // fresh presigned URL each fetch — refetching on window refocus would
      // change pdfUrl and force a full PDF reload (spinner). Keep it stable.
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

export const useSubmitSessionDocument = () =>
  useMutation(async ({ token, envelopeId, fieldValues, consent, signerName }) => {
    const res = await axios.post(
      `/public/sign/${token}/documents/${envelopeId}/submit`,
      { fieldValues, consent, signerName },
      { timeout: SIGN_SUBMIT_TIMEOUT_MS },
    );
    return res.data;
  });
